// RFC 8187 extended value: charset'language'percent-encoded-octets
const EXTENDED_VALUE = /^([^';\s]*)'[^']*'([^\s]+)$/
const QUOTED_VALUE = /^"((?:[^"\\]|\\.)*)"$/

/**
 * Splits a Content-Disposition value into its parameters, on `;` outside RFC 6266
 * quoted-strings. A regex cannot track the quote and escape state, hence the scan. Names are
 * lower-cased, the first occurrence of a name wins, and values keep their surrounding quotes.
 */
const dispositionParameters = (disposition: string): Map<string, string> => {
  const parts: string[] = []
  let start = 0
  let quoted = false
  let escaped = false

  for (let index = 0; index < disposition.length; index += 1) {
    const character = disposition[index]
    if (escaped) {
      escaped = false
    } else if (quoted && character === '\\') {
      escaped = true
    } else if (character === '"') {
      quoted = !quoted
    } else if (!quoted && character === ';') {
      parts.push(disposition.slice(start, index))
      start = index + 1
    }
  }
  parts.push(disposition.slice(start))

  const parameters = new Map<string, string>()
  for (const part of parts) {
    const equals = part.indexOf('=')
    if (equals < 0) {
      continue
    }
    const name = part.slice(0, equals).trim().toLowerCase()
    if (!parameters.has(name)) {
      parameters.set(name, part.slice(equals + 1).trim())
    }
  }
  return parameters
}

/**
 * Reads the filename from a Content-Disposition header value (RFC 6266).
 *
 * Prefers the RFC 8187 `filename*` parameter, which carries names outside US-ASCII as
 * percent-encoded UTF-8, over the plain `filename` parameter that servers send as an ASCII
 * fallback. A bare `filename=` token, as older endpoints send it, is still accepted.
 * Ex: `attachment; filename="_.csv"; filename*=UTF-8''%E6%A0%AA.csv` => '株.csv'
 */
export const filenameFromContentDisposition = (
  disposition: string | null | undefined,
): string | undefined => {
  if (!disposition) {
    return undefined
  }
  const parameters = dispositionParameters(disposition)
  const extended = EXTENDED_VALUE.exec(parameters.get('filename*') ?? '')
  if (extended?.[1].toUpperCase() === 'UTF-8') {
    try {
      return decodeURIComponent(extended[2])
    } catch {
      // Malformed percent-encoding: fall back to the plain parameter.
    }
  }
  const filename = parameters.get('filename')
  if (!filename) {
    return undefined
  }
  const quoted = QUOTED_VALUE.exec(filename)
  if (quoted) {
    return quoted[1].replace(/\\(.)/g, '$1') || undefined
  }
  return filename
}
